import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { Lock, X, Delete, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

export const StaffPinCard = ({ staffId }) => {
  const [pinSet, setPinSet] = useState(null);
  const [pin, setPin] = useState("");
  const load = () => staffId && api.get("/switch/users").then(({ data }) => { const u = data.find((x) => x.id === staffId); setPinSet(!!u?.pin_set); }).catch(() => {});
  useEffect(() => { load(); }, [staffId]);
  const save = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be 4 digits"); return; }
    try { await api.post("/switch/pin", { user_id: staffId, pin }); toast.success("PIN set"); setPin(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set PIN"); }
  };
  const remove = async () => { try { await api.delete(`/switch/pin/${staffId}`); toast.success("PIN removed"); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="rounded-2xl border p-4" data-testid="staff-pin-card">
      <div className="text-sm font-medium">Quick-switch PIN</div>
      <p className="text-xs text-muted-foreground mb-3">A 4-digit PIN this person uses to switch to their profile on a shared device. Stored securely (can't be viewed — reset it if forgotten).</p>
      {!staffId ? (
        <p className="text-xs text-muted-foreground">Save this staff member first, then set their PIN.</p>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${pinSet ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>{pinSet === null ? "…" : pinSet ? "PIN set" : "No PIN"}</span>
          <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="• • • •" className="h-10 w-28 rounded-lg border border-border bg-background px-3 text-center tracking-[0.4em]" data-testid="staff-pin-input" />
          <button onClick={save} className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium" data-testid="staff-pin-save">{pinSet ? "Reset PIN" : "Set PIN"}</button>
          {pinSet && <button onClick={remove} className="h-10 px-3 rounded-lg border border-border text-sm text-muted-foreground hover:text-destructive" data-testid="staff-pin-remove">Remove</button>}
        </div>
      )}
    </div>
  );
};

export const UserSwitchOverlay = ({ lockMode = false, onClose, onDone }) => {
  const { switchUser, user } = useAuth();
  const [users, setUsers] = useState(null);
  const [sel, setSel] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/switch/users").then(({ data }) => {
      setUsers(data);
      if (lockMode && user) { const me = data.find((u) => u.id === user.id); if (me?.pin_set) setSel(me); }
    }).catch(() => setUsers([]));
    // eslint-disable-next-line
  }, []);

  const submit = async (code) => {
    if (!sel || busy) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/switch/login", { user_id: sel.id, pin: code });
      await switchUser(data.token);
      toast.success(`Signed in as ${data.user.name}`);
      onDone?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Incorrect PIN");
      setPin("");
    } finally { setBusy(false); }
  };

  const tap = (d) => {
    if (pin.length >= 4 || busy) return;
    const next = pin + d;
    setPin(next); setErr("");
    if (next.length === 4) submit(next);
  };

  useEffect(() => {
    if (!sel) return;
    const onKey = (e) => {
      if (e.key >= "0" && e.key <= "9") { e.preventDefault(); tap(e.key); }
      else if (e.key === "Backspace") { e.preventDefault(); setPin((p) => p.slice(0, -1)); setErr(""); }
      else if (e.key === "Escape") { setSel(null); setPin(""); setErr(""); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [sel, pin, busy]);

  return (
    <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="user-switch-overlay">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {lockMode ? <Lock className="h-5 w-5 text-primary" /> : null}
            <h2 className="font-heading font-extrabold text-2xl">{lockMode ? "Locked" : "Switch user"}</h2>
          </div>
          {!lockMode && <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="switch-close"><X className="h-6 w-6" /></button>}
        </div>

        {!sel ? (
          <>
            <p className="text-muted-foreground text-sm mb-4">{lockMode ? "Enter your PIN to continue, or pick another user." : "Tap your name, then enter your 4-digit PIN."}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="switch-user-grid">
              {users === null && <div className="col-span-3 text-center text-muted-foreground py-8">Loading…</div>}
              {users?.map((u) => (
                <button key={u.id} disabled={!u.pin_set} onClick={() => { setSel(u); setPin(""); setErr(""); }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-colors ${u.pin_set ? "border-border hover:border-primary/60 hover:bg-muted/50" : "border-dashed border-border opacity-50 cursor-not-allowed"}`}
                  data-testid={`switch-user-${u.id}`}>
                  <span className="h-14 w-14 rounded-full flex items-center justify-center font-bold text-white text-lg" style={{ background: u.color }}>{initials(u.name)}</span>
                  <span className="text-sm font-medium leading-tight text-center">{u.name}</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{u.pin_set ? u.role : "No PIN set"}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center">
            <span className="h-16 w-16 rounded-full flex items-center justify-center font-bold text-white text-xl mb-2" style={{ background: sel.color }}>{initials(sel.name)}</span>
            <div className="font-semibold text-lg">{sel.name}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-5">{sel.role}</div>
            <div className="flex gap-3 mb-2" data-testid="pin-dots">
              {[0, 1, 2, 3].map((i) => <span key={i} className={`h-4 w-4 rounded-full border-2 ${i < pin.length ? "bg-primary border-primary" : "border-muted-foreground/40"}`} />)}
            </div>
            <div className="h-5 text-sm text-destructive mb-2" data-testid="pin-error">{err}</div>
            <div className="grid grid-cols-3 gap-3 w-64">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <button key={d} onClick={() => tap(String(d))} className="h-16 rounded-2xl bg-muted hover:bg-muted/70 text-2xl font-semibold active:scale-95 transition-transform" data-testid={`pin-key-${d}`}>{d}</button>
              ))}
              <button onClick={() => { setSel(null); setPin(""); setErr(""); }} className="h-16 rounded-2xl flex items-center justify-center text-muted-foreground hover:text-foreground" data-testid="pin-back"><ArrowLeft className="h-5 w-5" /></button>
              <button onClick={() => tap("0")} className="h-16 rounded-2xl bg-muted hover:bg-muted/70 text-2xl font-semibold active:scale-95 transition-transform" data-testid="pin-key-0">0</button>
              <button onClick={() => setPin((p) => p.slice(0, -1))} className="h-16 rounded-2xl flex items-center justify-center text-muted-foreground hover:text-foreground" data-testid="pin-del"><Delete className="h-5 w-5" /></button>
            </div>
            {busy && <div className="text-sm text-muted-foreground mt-3">Checking…</div>}
          </div>
        )}
      </div>
    </div>
  );
};
